import React, { createContext, useContext, useMemo } from 'react';
import { useAuthenticatedUserInfo } from './AuthenticatedUserInfoContext';
import { useSubscription } from '../../hooks/useSubscription';
import { usePlans } from '../../hooks/usePlan';
import { useFeatureLimitsForPlan } from '../../hooks/useFeatureLimits';
import {
	createFeatureChecker,
	normalizeFeatureAccessOptions,
	normalizeFeatureRecord,
} from '../../utils/featureUtils';

const PlanFeatureAccessContext = createContext(null);

const matchActivePlan = (allPlans, subscription) => {
	if (!allPlans?.length || !subscription) return null;

	if (subscription.planId) {
		const byId = allPlans.find(
			(plan) => String(plan._id) === String(subscription.planId),
		);
		if (byId) return byId;
	}

	const matchedByPrice = allPlans.find(
		(plan) =>
			plan.stripePriceIdMonthly === subscription.subscriptionPriceId ||
			plan.stripePriceIdYearly === subscription.subscriptionPriceId ||
			plan.stripePriceIdOneTime === subscription.subscriptionPriceId,
	);
	if (matchedByPrice) return matchedByPrice;

	return (
		allPlans.find(
			(plan) =>
				subscription.planName &&
				(plan.planName || '').toLowerCase() === subscription.planName.toLowerCase(),
		) || null
	);
};

export const PlanFeatureAccessProvider = ({ children }) => {
	const { companyId, userRole, isSystemAdmin, token, user, loading } =
		useAuthenticatedUserInfo();
	const { data: subscription, isLoading: subscriptionLoading } = useSubscription(companyId);
	const { data: allPlans, isLoading: plansLoading } = usePlans({
		enabled: !!token && !!user && !loading,
	});

	const matchedPlan = useMemo(
		() => matchActivePlan(allPlans, subscription),
		[allPlans, subscription],
	);

	const { data: featureLimitsResponse, isLoading: limitsLoading } =
		useFeatureLimitsForPlan(matchedPlan?._id);

	const accessOptions = useMemo(
		() =>
			normalizeFeatureAccessOptions({
				isSystemAdmin,
				userRole,
			}),
		[isSystemAdmin, userRole],
	);

	const limitsMap = useMemo(() => {
		const apiData = featureLimitsResponse?.data?.data || {};
		return Object.fromEntries(
			Object.entries(apiData).map(([key, row]) => [key, normalizeFeatureRecord(row)]),
		);
	}, [featureLimitsResponse]);

	const checker = useMemo(
		() => createFeatureChecker(limitsMap, accessOptions),
		[limitsMap, accessOptions],
	);

	const value = useMemo(() => {
		const planName = matchedPlan?.planName ?? subscription?.planName ?? null;
		const hasSubscription =
			!!subscription?.isActive || subscription?.status === 'active';

		const getFeatureLimit = (featureKey) => {
			const feature = checker.getFeature(featureKey);
			return {
				...feature,
				planName,
				planId: matchedPlan?._id ?? null,
				hasSubscription,
			};
		};

		return {
			planName,
			planId: matchedPlan?._id ?? null,
			hasSubscription,
			limits: limitsMap,
			accessOptions,
			isLoading: subscriptionLoading || plansLoading || limitsLoading,
			canUse: checker.canUse,
			getFeature: checker.getFeature,
			isAtLimit: checker.isAtLimit,
			hasQuota: checker.hasQuota,
			getUsageRatio: checker.getUsageRatio,
			isEnforcementActive: checker.isEnforcementActive,
			getFeatureLimit,
		};
	}, [
		checker,
		limitsMap,
		matchedPlan,
		subscription,
		subscriptionLoading,
		plansLoading,
		limitsLoading,
		accessOptions,
	]);

	return (
		<PlanFeatureAccessContext.Provider value={value}>
			{children}
		</PlanFeatureAccessContext.Provider>
	);
};

export const usePlanFeatureAccessContext = () => {
	const context = useContext(PlanFeatureAccessContext);
	if (!context) {
		throw new Error(
			'usePlanFeatureAccess must be used within a PlanFeatureAccessProvider',
		);
	}
	return context;
};

export default PlanFeatureAccessContext;
